import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../state/AuthContext';
import { listAccessibleChildren } from '../lib/quizRepository';
import type { QuizChild } from '../lib/quizRepository';
import {
  finishMathTablePractice,
  loadMathPracticeQuestions,
  loadMathTableProgress,
  startMathTablePractice,
  submitMathPracticeAnswer,
} from '../lib/mathTablesRepository';
import type {
  MathAnswerFeedback,
  MathPracticeMode,
  MathPracticeQuestion,
  MathPracticeResult,
  MathTableProgress,
} from '../lib/mathTablesRepository';

const practiceModes: Array<{ id: MathPracticeMode; title: string; description: string; example: string }> = [
  { id: 'forward_recall', title: 'Times table', description: 'Recall the product.', example: '4 × 6 = ?' },
  { id: 'missing_factor', title: 'Missing number', description: 'Find the hidden multiplier.', example: '4 × ? = 24' },
  { id: 'division_link', title: 'Division link', description: 'Connect multiplication and division.', example: '24 ÷ 4 = ?' },
];

const tableNumbers = Array.from({ length: 30 }, (_, index) => index + 1);
const questionCounts = [6, 12, 18, 24];

function questionPrompt(question: MathPracticeQuestion) {
  if (question.practiceMode === 'missing_factor') {
    return <>{question.tableNumber} × <span className="math-blank">?</span> = {question.product}</>;
  }
  if (question.practiceMode === 'division_link') {
    return <>{question.product} ÷ {question.tableNumber} = <span className="math-blank">?</span></>;
  }
  return <>{question.tableNumber} × {question.multiplier} = <span className="math-blank">?</span></>;
}

function ProgressGrid({ progress, from, to }: { progress: MathTableProgress[]; from: number; to: number }) {
  return (
    <div className="math-progress-grid">
      {progress.filter((item) => item.tableNumber >= from && item.tableNumber <= to).map((item) => (
        <article key={item.tableNumber} className={item.masteredFacts === item.totalFacts ? 'mastered' : ''}>
          <strong>× {item.tableNumber}</strong>
          <span>{item.totalAttempts ? `${item.accuracyPercent}% accurate` : 'Not started'}</span>
          <progress value={item.secureFacts} max={item.totalFacts} />
          <small>{item.secureFacts}/{item.totalFacts} facts secure</small>
        </article>
      ))}
    </div>
  );
}

function MathResultCard({ result, onRestart }: { result: MathPracticeResult; onRestart(): void }) {
  const minutes = Math.floor(result.durationSeconds / 60);
  const seconds = result.durationSeconds % 60;
  return (
    <section className="math-result panel">
      <img src="/brand/piko-parrot-mascot.png" alt="Piko parrot mascot" />
      <p className="eyebrow">Practice complete</p>
      <h2>{result.percentage >= 90 ? 'Table champion!' : result.percentage >= 70 ? 'Great practising!' : 'Keep building your speed!'}</h2>
      <strong>{result.score} / {result.questionCount}</strong>
      <p>{result.percentage}% correct · {minutes}:{String(seconds).padStart(2, '0')}</p>
      <span className="pill green">Result and mastery saved securely</span>
      <button className="button" type="button" onClick={onRestart}>Practise again</button>
    </section>
  );
}

export function MathTablesPage() {
  const { user, isSupabaseConfigured } = useAuth();
  const [children, setChildren] = useState<QuizChild[]>([]);
  const [childId, setChildId] = useState('');
  const [progress, setProgress] = useState<MathTableProgress[]>([]);
  const [selectedTables, setSelectedTables] = useState<number[]>([2]);
  const [practiceMode, setPracticeMode] = useState<MathPracticeMode>('forward_recall');
  const [questionCount, setQuestionCount] = useState(12);
  const [sessionId, setSessionId] = useState('');
  const [questions, setQuestions] = useState<MathPracticeQuestion[]>([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState<MathAnswerFeedback | null>(null);
  const [questionStartedAt, setQuestionStartedAt] = useState(Date.now());
  const [result, setResult] = useState<MathPracticeResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const selectedChild = children.find((child) => child.id === childId);
  const currentQuestion = questions[questionIndex];
  const effectiveQuestionCount = Math.min(questionCount, selectedTables.length * 12);
  const totals = useMemo(() => progress.reduce((summary, item) => ({
    attempts: summary.attempts + item.totalAttempts,
    correct: summary.correct + item.correctAttempts,
    secure: summary.secure + item.secureFacts,
    mastered: summary.mastered + item.masteredFacts,
  }), { attempts: 0, correct: 0, secure: 0, mastered: 0 }), [progress]);

  useEffect(() => {
    if (!isSupabaseConfigured || user?.demo) return;
    let active = true;
    setBusy(true);
    void listAccessibleChildren()
      .then((items) => {
        if (!active) return;
        setChildren(items);
        setChildId((current) => current || items[0]?.id || '');
      })
      .catch((error: unknown) => active && setMessage(error instanceof Error ? error.message : 'Unable to load learners.'))
      .finally(() => active && setBusy(false));
    return () => { active = false; };
  }, [isSupabaseConfigured, user?.demo]);

  useEffect(() => {
    if (!childId || !isSupabaseConfigured || user?.demo) return;
    let active = true;
    void loadMathTableProgress(childId)
      .then((items) => active && setProgress(items))
      .catch((error: unknown) => active && setMessage(error instanceof Error ? error.message : 'Unable to load table progress.'));
    return () => { active = false; };
  }, [childId, isSupabaseConfigured, user?.demo]);

  function toggleTable(value: number) {
    setMessage('');
    setSelectedTables((current) => {
      if (current.includes(value)) return current.filter((table) => table !== value);
      if (current.length >= 12) {
        setMessage('Choose up to 12 tables in one practice session.');
        return current;
      }
      return [...current, value].sort((a, b) => a - b);
    });
  }

  async function beginPractice() {
    if (!childId || selectedTables.length === 0) return;
    setBusy(true);
    setMessage('');
    try {
      const newSessionId = await startMathTablePractice(childId, selectedTables, practiceMode, effectiveQuestionCount);
      const sessionQuestions = await loadMathPracticeQuestions(newSessionId);
      if (!sessionQuestions.length) throw new Error('No multiplication facts were available for this session.');
      setSessionId(newSessionId);
      setQuestions(sessionQuestions);
      setQuestionIndex(0);
      setAnswer('');
      setFeedback(null);
      setQuestionStartedAt(Date.now());
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to start table practice.');
    } finally {
      setBusy(false);
    }
  }

  async function checkAnswer(event: FormEvent) {
    event.preventDefault();
    if (!currentQuestion || answer.trim() === '') return;
    const numericAnswer = Number(answer);
    if (!Number.isInteger(numericAnswer)) {
      setMessage('Enter a whole number.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      setFeedback(await submitMathPracticeAnswer(
        sessionId,
        currentQuestion.questionNumber,
        numericAnswer,
        Date.now() - questionStartedAt,
      ));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to check the answer.');
    } finally {
      setBusy(false);
    }
  }

  async function nextQuestion() {
    if (questionIndex < questions.length - 1) {
      setQuestionIndex((current) => current + 1);
      setAnswer('');
      setFeedback(null);
      setMessage('');
      setQuestionStartedAt(Date.now());
      return;
    }
    setBusy(true);
    try {
      const finished = await finishMathTablePractice(sessionId);
      setResult(finished);
      setProgress(await loadMathTableProgress(childId));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to finish table practice.');
    } finally {
      setBusy(false);
    }
  }

  function resetPractice() {
    setSessionId('');
    setQuestions([]);
    setQuestionIndex(0);
    setAnswer('');
    setFeedback(null);
    setResult(null);
    setMessage('');
  }

  if (!isSupabaseConfigured || user?.demo) {
    return (
      <div className="portal-page">
        <section className="empty-state">
          <img src="/brand/piko-parrot-mascot.png" alt="Piko parrot mascot" />
          <h3>Math Tables Centre requires a real staging login</h3>
          <p>Demo roles cannot save practice sessions or learner mastery.</p>
        </section>
      </div>
    );
  }

  if (result) return <div className="portal-page"><MathResultCard result={result} onRestart={resetPractice} /></div>;

  if (sessionId && currentQuestion) {
    return (
      <div className="portal-page math-page">
        <header className="page-header">
          <div>
            <p className="eyebrow">Math Tables Centre</p>
            <h1>{selectedChild?.displayName}’s table practice</h1>
            <p>Answers and mastery are verified and saved by Supabase staging.</p>
          </div>
          <span className="pill green">Server-verified scoring</span>
        </header>
        {message && <div className="form-message error quiz-message">{message}</div>}
        {busy && <div className="quiz-busy">Piko is checking…</div>}
        <section className="math-practice-stage">
          <div className="quiz-progress-row">
            <span>Question {questionIndex + 1} of {questions.length}</span>
            <progress value={questionIndex + (feedback ? 1 : 0)} max={questions.length} />
            <span>{feedback?.currentScore ?? questionIndex} correct</span>
          </div>
          <form className="math-question-card" onSubmit={(event) => void checkAnswer(event)}>
            <span className="pill purple">{practiceModes.find((mode) => mode.id === currentQuestion.practiceMode)?.title}</span>
            <div className="math-equation">{questionPrompt(currentQuestion)}</div>
            <label>
              Your answer
              <input
                key={currentQuestion.questionNumber}
                inputMode="numeric"
                pattern="[0-9]*"
                value={answer}
                disabled={Boolean(feedback)}
                onChange={(event) => setAnswer(event.target.value.replace(/[^0-9-]/g, ''))}
                autoFocus
                aria-label="Your table answer"
              />
            </label>
            {feedback && (
              <div className={`answer-feedback ${feedback.isCorrect ? 'correct' : 'incorrect'}`}>
                <strong>{feedback.isCorrect ? 'Correct—well done!' : `Not this time. The answer is ${feedback.correctAnswer}.`}</strong>
                <span>{feedback.explanation}</span>
              </div>
            )}
            <div className="quiz-footer-actions">
              {!feedback
                ? <button className="button" type="submit" disabled={busy || answer.trim() === ''}>Check answer</button>
                : <button className="button" type="button" onClick={() => void nextQuestion()}>{questionIndex === questions.length - 1 ? 'Finish practice' : 'Next question'}</button>}
            </div>
          </form>
        </section>
      </div>
    );
  }

  return (
    <div className="portal-page math-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Math Tables Centre</p>
          <h1>Build fast, confident table recall</h1>
          <p>Practise tables 1–30 in three modes. Every answer updates the learner’s secure mastery record.</p>
        </div>
        <span className="pill green">360 facts ready</span>
      </header>

      {message && <div className="form-message error quiz-message">{message}</div>}
      {busy && <div className="quiz-busy">Piko is preparing the tables…</div>}

      <section className="math-summary-grid">
        <article><span>Total attempts</span><strong>{totals.attempts}</strong></article>
        <article><span>Accuracy</span><strong>{totals.attempts ? Math.round(100 * totals.correct / totals.attempts) : 0}%</strong></article>
        <article><span>Secure facts</span><strong>{totals.secure}/360</strong></article>
        <article><span>Mastered facts</span><strong>{totals.mastered}/360</strong></article>
      </section>

      <section className="math-setup-grid">
        <aside className="panel math-controls">
          <label>
            Learner
            <select value={childId} onChange={(event) => setChildId(event.target.value)}>
              {children.map((child) => <option key={child.id} value={child.id}>{child.displayName} · {child.levelCode ?? 'Level not set'}</option>)}
            </select>
          </label>
          {!children.length && <p>No accessible learner profile was found.</p>}

          <fieldset>
            <legend>Practice mode</legend>
            <div className="math-mode-grid">
              {practiceModes.map((mode) => (
                <button key={mode.id} className={practiceMode === mode.id ? 'selected' : ''} type="button" onClick={() => setPracticeMode(mode.id)}>
                  <strong>{mode.title}</strong>
                  <span>{mode.description}</span>
                  <small>{mode.example}</small>
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend>Number of questions</legend>
            <div className="math-count-buttons">
              {questionCounts.map((count) => <button key={count} className={questionCount === count ? 'selected' : ''} type="button" onClick={() => setQuestionCount(count)}>{count}</button>)}
            </div>
          </fieldset>

          <button className="button math-start" type="button" disabled={!childId || selectedTables.length === 0 || busy} onClick={() => void beginPractice()}>
            Start {effectiveQuestionCount}-question practice
          </button>
        </aside>

        <div className="panel math-table-picker">
          <div className="panel-heading">
            <div><p className="eyebrow">Choose tables</p><h2>{selectedTables.length} selected</h2></div>
            <button className="button secondary small" type="button" onClick={() => setSelectedTables([2])}>Reset</button>
          </div>
          <div className="math-quick-picks">
            <button type="button" onClick={() => setSelectedTables([2, 3, 4, 5])}>Tables 2–5</button>
            <button type="button" onClick={() => setSelectedTables(Array.from({ length: 11 }, (_, index) => index + 2))}>Tables 2–12</button>
            <button type="button" onClick={() => setSelectedTables(Array.from({ length: 8 }, (_, index) => index + 13))}>Tables 13–20</button>
          </div>
          <div className="math-table-buttons" role="group" aria-label="Multiplication tables">
            {tableNumbers.map((value) => (
              <button key={value} type="button" className={selectedTables.includes(value) ? 'selected' : ''} aria-pressed={selectedTables.includes(value)} onClick={() => toggleTable(value)}>
                × {value}
              </button>
            ))}
          </div>
          <p className="table-note">Tables 1–20 are the core centre. Tables 21–30 are available for advanced practice.</p>
        </div>
      </section>

      <section className="panel math-progress-panel">
        <div className="panel-heading"><div><p className="eyebrow">Learner progress</p><h2>Tables 1–20</h2></div></div>
        <ProgressGrid progress={progress} from={1} to={20} />
        <details>
          <summary>Show advanced tables 21–30</summary>
          <ProgressGrid progress={progress} from={21} to={30} />
        </details>
      </section>
    </div>
  );
}
